import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSelectFormById } from "@/features/forms/api/selectFormById";
import { PrevalenceBadge } from "@/features/forms/components/PrevalenceBadge";
import { ScoringBadge } from "@/features/forms/components/ScoringBadge";
import type { FormViewT } from "@/types/database/forms";
import { createFileRoute } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import { Clock, FileText, HelpCircle } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

type PageWithInputs = { inputs?: unknown[] };

const FormLandingPage = () => {
	const { formId } = Route.useParams();
	const navigate = useNavigate();
	const { data: form, isLoading } = useSelectFormById(formId);

	if (isLoading) {
		return (
			<div className="space-y-4 py-16 md-width">
				<Skeleton className="w-3/4 h-8" />
				<Skeleton className="w-full h-4" />
				<Skeleton className="w-2/3 h-4" />
				<Skeleton className="mt-4 w-32 h-10" />
			</div>
		);
	}

	if (!form) return null;

	const f = form as Partial<FormViewT>;

	// Compute number of pages and questions
	const numberOfPages = Array.isArray(f.pages) ? f.pages.length : 0;
	const numberOfQuestions = Array.isArray(f.pages)
		? f.pages.reduce(
			(acc: number, page: PageWithInputs) =>
				Array.isArray(page?.inputs) ? acc + page.inputs.length : acc,
			0,
		)
		: 0;

	return (
		<div className="flex justify-center mx-auto px-2 py-12 max-w-lg">
			<div className="flex flex-col gap-6 bg-white shadow-lg p-8 rounded-xl w-full max-w-2xl">
				<div className="flex flex-col gap-2">
					<h1 className="flex items-center gap-3 font-bold text-3xl">
						{f.form_name}
						<PrevalenceBadge prevalence={f.prevalence} />
						<ScoringBadge scoring={f.scoring} />
					</h1>
					<p className="text-muted-foreground text-lg">
						{f.form_description}
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					{Array.isArray(f.orthopaedic_areas) &&
						f.orthopaedic_areas?.map((area: string) => (
							<Badge key={area} color="cyan">
								{area}
							</Badge>
						))}
				</div>
				<div className="flex flex-wrap gap-4 text-slate-700 text-sm">
					<div className="flex items-center gap-2">
						<Clock className="w-4 h-4 text-slate-500" />
						<span className="font-semibold">Time to Complete:</span>
						<span>
							{f.time_to_complete
								? `${f.time_to_complete} min`
								: "-"}
						</span>
					</div>
					<div className="flex items-center gap-2">
						<FileText className="w-4 h-4 text-slate-500" />
						<span className="font-semibold">Pages:</span>
						<span>{numberOfPages}</span>
					</div>
					<div className="flex items-center gap-2">
						<HelpCircle className="w-4 h-4 text-slate-500" />
						<span className="font-semibold">Questions:</span>
						<span>{numberOfQuestions}</span>
					</div>
				</div>
				{f.form_instructions && (
					<div className="bg-slate-50 p-4 border border-slate-200 rounded-md text-slate-800">
						<span className="font-semibold">Instructions:</span>
						<div className="mt-1 whitespace-pre-line">
							{f.form_instructions}
						</div>
					</div>
				)}
				<Button
					className="mt-4 py-6 w-full text-lg"
					onClick={() =>
						navigate({
							to: "/public/form/$formId/entry/$entryId/page/$pageIndex",
							params: {
								formId,
								entryId: uuidv4(),
								pageIndex: "1",
							},
						})}
				>
					Start Form
				</Button>
			</div>
		</div>
	);
};

export const Route = createFileRoute("/public/form/$formId/")({
	component: FormLandingPage,
});
